import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const normalize = (p) => {
  if (!p) return '';
  let path = p.split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) path = path.replace(/\/+$/, '');
  return path;
};

/**
 * RedirectManager — server-driven 301-style redirect handler for the SPA.
 * The redirect DECISION is made by the backend (GET /api/redirects/check),
 * which IS proxied to the backend in production, unlike root-level routes.
 * On a match we render a canonical + meta-refresh (for crawlers) and then
 * window.location.replace() to the destination. Admin/api paths are skipped.
 */
const RedirectManager = () => {
  const location = useLocation();
  const checkedRef = useRef('');

  useEffect(() => {
    const path = normalize(location.pathname);
    if (!path || path.startsWith('/admin') || path.startsWith('/api')) return;
    if (checkedRef.current === path) return;
    checkedRef.current = path;

    fetch(`${API_URL}/api/redirects/check?path=${encodeURIComponent(path)}`)
      .then((r) => r.json())
      .then((d) => {
        if (!d || !d.redirect || !d.target) return;
        const target = d.target;
        if (normalize(target) === path) return;

        // Server-driven decision — expose it to crawlers via canonical + meta refresh
        try {
          const canonical = document.createElement('link');
          canonical.rel = 'canonical';
          canonical.href = `https://fortworthdental.com${target}`;
          document.head.appendChild(canonical);

          const meta = document.createElement('meta');
          meta.httpEquiv = 'refresh';
          meta.content = `0; url=${target}`;
          document.head.appendChild(meta);
        } catch (e) { /* noop */ }

        // fire-and-forget hit counter
        try {
          fetch(`${API_URL}/api/redirects/hit?source_path=${encodeURIComponent(path)}`, { method: 'POST' });
        } catch (e) { /* noop */ }

        // Full replace so the address bar + crawlers land on the target
        window.location.replace(target);
      })
      .catch(() => { /* noop */ });
  }, [location.pathname]);

  return null;
};

export default RedirectManager;
